import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button, Input, Select } from '@atoms';
import { trpc } from '@lib/trpc';
import toast from 'react-hot-toast';

interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
}

interface AdminRoleModalProps {
  role: Role | null;
  isEditing: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoleFormData {
  name: string;
  displayName: string;
  description: string;
  color: string;
  isActive: boolean;
}

interface ValidationErrors {
  name?: string;
  displayName?: string;
  description?: string;
  color?: string;
}

const PREDEFINED_COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Yellow' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#F97316', label: 'Orange' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6B7280', label: 'Gray' },
];

const STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export const AdminRoleModal: React.FC<AdminRoleModalProps> = ({
  role,
  isEditing,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    displayName: '',
    description: '',
    color: PREDEFINED_COLORS[0].value,
    isActive: true,
  });
  
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createRoleMutation = trpc.admin.createRole.useMutation({
    onSuccess: () => {
      toast.success('Role created successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create role');
      setIsSubmitting(false);
    },
  });

  const updateRoleMutation = trpc.admin.updateRole.useMutation({
    onSuccess: () => {
      toast.success('Role updated successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update role');
      setIsSubmitting(false);
    },
  });

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && role) {
      setFormData({
        name: role.name,
        displayName: role.displayName,
        description: role.description || '',
        color: role.color || PREDEFINED_COLORS[0].value,
        isActive: role.isActive,
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        description: '',
        color: PREDEFINED_COLORS[0].value,
        isActive: true,
      });
    }
    setErrors({});
  }, [isEditing, role]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Role name must contain only lowercase letters, numbers, and underscores';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    } else if (formData.displayName.length < 2) {
      newErrors.displayName = 'Display name must be at least 2 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof RoleFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing && role) {
        await updateRoleMutation.mutateAsync({
          id: role.id,
          data: {
            name: formData.name,
            displayName: formData.displayName,
            description: formData.description || undefined,
            color: formData.color || undefined,
            isActive: formData.isActive,
          },
        });
      } else {
        await createRoleMutation.mutateAsync({
          name: formData.name,
          displayName: formData.displayName,
          description: formData.description || undefined,
          color: formData.color || undefined,
          isActive: formData.isActive,
        });
      }
    } catch (error) {
      // Error handling is done in mutation callbacks
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-primary rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border-primary">
          <h2 className="text-xl font-semibold text-text-primary">
            {isEditing ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Role Name */}
          <div>
            <Input
              label="Role Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              errorMessage={errors.name}
              placeholder="e.g., admin, user, editor"
              helperText="Lowercase letters, numbers, and underscores only. Used internally."
              disabled={isSubmitting}
              fullWidth
            />
          </div>

          {/* Display Name */}
          <div>
            <Input
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              error={!!errors.displayName}
              errorMessage={errors.displayName}
              placeholder="e.g., Administrator, User, Content Editor"
              helperText="User-friendly name shown in the interface."
              disabled={isSubmitting}
              fullWidth
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 resize-none ${
                errors.description
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                  : 'border-secondary-300 focus:border-primary-500 focus:ring-primary-200'
              }`}
              rows={3}
              placeholder="Optional description of what this role can do..."
              disabled={isSubmitting}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
            <p className="mt-1 text-sm text-text-secondary">
              {formData.description.length}/500 characters
            </p>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Color
            </label>
            <div className="flex items-center space-x-3">
              <div
                className="w-8 h-8 rounded-full border-2 border-border-primary"
                style={{ backgroundColor: formData.color }}
              />
              <Select
                options={PREDEFINED_COLORS.map(color => ({
                  value: color.value,
                  label: color.label,
                }))}
                value={formData.color}
                onChange={(value) => handleInputChange('color', value)}
                className="flex-1"
                disabled={isSubmitting}
              />
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              Color used to visually distinguish this role.
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Status
            </label>
            <Select
              options={STATUS_OPTIONS}
              value={formData.isActive.toString()}
              onChange={(value) => handleInputChange('isActive', value === 'true')}
              disabled={isSubmitting}
            />
            <p className="mt-1 text-sm text-text-secondary">
              Inactive roles cannot be assigned to users.
            </p>
          </div>
        </form>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border-primary">
          <Button 
            variant="secondary" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditing ? 'Update Role' : 'Create Role'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
